// ---------------------------------------------------------------------------
// YouAreYou (uru) — Start Verification Page
//
// Warm KYC handoff interstitial. Rendered when a user taps an SMS invite link
// pointing to /start-verification/:uuid before being redirected into the
// MojoVerify Django KYC templates at /verify/basic/:uuid.
//
// Design source: screen-mockups/desktop/uru-request.html
//
// Layout: two-column split card
//   Left  — requester profile + their verified credential badges
//   Right — recipient (you) placeholder + "Start My Verification" CTA
//           + same credential list shown as pending/hidden
//
// Route:   /start-verification/:uuid
// Params:  uuid (path)   — MojoVerify verification session UUID
//          name (query)  — requester's full name, e.g. ?name=Jamie+Turner
//
// On CTA click: hard navigation (window.location.href) to
//   ${VITE_API_BASE_URL}/verify/basic/:uuid
//
// Sister screen: src/pages/public/ConnectionRequestPage.tsx
//   Shares the same two-panel card layout and visual design but is
//   auth-aware, makes an API call, and sends the unauthenticated CTA to
//   /register (SPA flow) rather than MojoVerify. If you update the card
//   layout, header, footer, or credential rows here, apply the same changes
//   there. Future refactor: extract shared presentational components into
//   src/pages/public/ConnectionCard/ and import from both.
// ---------------------------------------------------------------------------

import { useParams, useSearchParams, Link } from "react-router-dom";
import Icon from "@/components/ui/Icon";
import BrandName from "@/components/ui/BrandName";
import BrandMark from "@/components/ui/BrandMark";

const MOJO_BASE = import.meta.env.VITE_API_BASE_URL as string;

// ---------------------------------------------------------------------------
// Verification credential rows shown on both sides of the card
// ---------------------------------------------------------------------------

const CREDENTIALS = [
  { icon: "badge", label: "Government ID" },
  { icon: "face", label: "Liveness Scan" },
  { icon: "smartphone", label: "Phone Number" },
  { icon: "mail", label: "Email Address" },
  { icon: "workspace_premium", label: "Professional License" },
];

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function Header() {
  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/80 backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2">
          <BrandMark className="h-8 w-8" />
          <h1 className="text-xl font-black tracking-tight"><BrandName /></h1>
        </Link>

        {/* Center nav — desktop */}
        <nav className="hidden items-center gap-6 text-sm font-medium text-slate-600 md:flex">
          <Link to="/how-it-works" className="transition-colors hover:text-primary-500">
            How it works
          </Link>
          <Link to="/security" className="transition-colors hover:text-primary-500">
            Security
          </Link>
          <Link to="/support" className="transition-colors hover:text-primary-500">
            Help
          </Link>
        </nav>

        {/* Mobile hamburger */}
        <button className="rounded-lg p-2 hover:bg-slate-100 md:hidden" aria-label="Open menu">
          <Icon name="menu" className="text-xl text-slate-600" />
        </button>
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

function Footer() {
  return (
    <footer className="mt-auto border-t border-slate-200 py-10 text-center">
      <div className="mx-auto max-w-7xl px-6">
        <p className="text-sm font-medium text-slate-500">
          Secure, Encrypted Identity Verification by{" "}
          <BrandName />
        </p>
        <div className="mt-4 flex justify-center gap-6 text-xs text-slate-400">
          <Link to="/privacy" className="hover:underline">
            Privacy Policy
          </Link>
          <Link to="/terms" className="hover:underline">
            Terms of Service
          </Link>
          <Link to="/support" className="hover:underline">
            Contact Support
          </Link>
        </div>
      </div>
    </footer>
  );
}

// ---------------------------------------------------------------------------
// RequesterPanel — left half of the split card
// ---------------------------------------------------------------------------

function RequesterPanel({ name }: { name: string }) {
  // Derive initials for the avatar fallback
  const initials = name
    .split(" ")
    .map((part) => part[0]?.toUpperCase() ?? "")
    .slice(0, 2)
    .join("");

  return (
    <div className="flex flex-col items-center p-8 text-center md:p-12">
      {/* Avatar */}
      <div className="relative">
        <div className="flex h-32 w-32 items-center justify-center overflow-hidden rounded-full border-4 border-primary-200 bg-primary-50">
          <span className="text-4xl font-black text-primary-500">{initials}</span>
        </div>
        {/* Verified badge */}
        <div className="absolute bottom-1 right-1 rounded-full bg-white p-0.5">
          <Icon name="check_circle" filled className="text-2xl text-primary-500" />
        </div>
      </div>

      {/* Identity */}
      <div className="mt-4">
        <h3 className="text-xl font-bold text-slate-900">{name}</h3>
        <p className="flex items-center justify-center gap-1 text-sm font-semibold text-primary-500">
          <Icon name="verified" filled className="text-base text-primary-500" />
          <BrandName /> Verified
        </p>
        <p className="mt-1 text-xs font-bold uppercase tracking-widest text-slate-500">
          Requester
        </p>
      </div>

      {/* Verified credential list */}
      <div className="mt-10 w-full space-y-3">
        {CREDENTIALS.map((cred) => (
          <div
            key={cred.label}
            className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 p-4"
          >
            <div className="flex items-center gap-3">
              <Icon name={cred.icon} className="text-slate-500" />
              <span className="text-sm font-medium">{cred.label}</span>
            </div>
            <span className="rounded-full bg-green-100 px-2 py-1 text-[10px] font-bold uppercase text-green-700">
              Verified
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// RecipientPanel — right half of the split card
// ---------------------------------------------------------------------------

function RecipientPanel({ onStart }: { onStart: () => void }) {
  return (
    <div className="flex flex-col items-center bg-slate-50/50 p-8 text-center md:p-12">
      {/* Placeholder avatar */}
      <div className="relative">
        <div className="flex h-32 w-32 items-center justify-center overflow-hidden rounded-full border-4 border-dashed border-slate-300 bg-white">
          <Icon name="person" className="text-7xl text-slate-200" />
        </div>
        <div className="absolute bottom-1 right-1 rounded-full bg-white p-0.5">
          <Icon name="pending" className="text-2xl text-amber-500" />
        </div>
      </div>

      {/* Label */}
      <div className="mt-4">
        <h3 className="text-xl font-bold italic text-slate-400">You (Recipient)</h3>
      </div>

      {/* CTA */}
      <button
        onClick={onStart}
        className="mt-8 flex w-full items-center justify-center gap-3 rounded-2xl bg-primary-500 px-6 py-4 text-base font-bold text-white shadow-lg shadow-primary-500/30 transition-all hover:bg-primary-600 hover:shadow-xl active:scale-[0.98]"
      >
        <span>Start My Verification</span>
        <Icon name="arrow_forward" className="text-lg" />
      </button>

      {/* Pending credential list */}
      <div className="mt-10 w-full space-y-3">
        {CREDENTIALS.map((cred, i) => (
          <div
            key={cred.label}
            className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4 opacity-60"
          >
            <div className="flex items-center gap-3">
              <Icon name={cred.icon} className="text-slate-400" />
              <span className="text-sm font-medium text-slate-400">{cred.label}</span>
            </div>
            {/* First item (Liveness) shown as pending, rest as hidden */}
            {i === 1 ? (
              <span className="rounded-full bg-amber-100 px-2 py-1 text-[10px] font-bold uppercase text-amber-700">
                Pending
              </span>
            ) : (
              <span className="rounded-full bg-slate-100 px-2 py-1 text-[10px] font-bold uppercase text-slate-500">
                Hidden
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// StartVerificationPage — Main Component
// ---------------------------------------------------------------------------

export default function StartVerificationPage() {
  const { uuid } = useParams<{ uuid: string }>();
  const [searchParams] = useSearchParams();

  const requesterName = searchParams.get("name")?.trim() || "Someone";
  const verifyUrl = `${MOJO_BASE}/verify/basic/${uuid ?? ""}`;

  function handleStart() {
    window.location.href = verifyUrl;
  }

  return (
    <div className="flex min-h-dvh flex-col bg-background-light font-sans text-slate-900">
      <Header />

      <main className="flex flex-grow flex-col items-center px-6 py-12">
        {/* Headline */}
        <div className="mb-12 max-w-4xl w-full text-center">
          <h2 className="text-3xl font-extrabold leading-tight tracking-tight text-slate-900 md:text-5xl">
            {requesterName} wants to know{" "}
            <BrandName />
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            Request for secure identity verification
          </p>
        </div>

        {/* Split card */}
        <div className="relative w-full max-w-6xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl shadow-slate-200/50">
          {/* Vertical divider — desktop only */}
          <div className="absolute left-1/2 top-10 bottom-10 hidden w-px bg-slate-200 md:block" />

          <div className="grid grid-cols-1 md:grid-cols-2">
            <RequesterPanel name={requesterName} />
            <RecipientPanel onStart={handleStart} />
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
